/**
 * Vue 3 port of the `v-click-outside` package (v3.2.0), which only ships the
 * Vue 2 hooks (bind/update/unbind) and is therefore never called by Vue 3.
 *
 * The binding value is either a handler function or a config object:
 *   { handler, middleware, events, isActive, detectIframe, capture }
 */

const HANDLERS_PROPERTY = '__v-click-outside';
const HAS_WINDOW = typeof window !== 'undefined';
const HAS_NAVIGATOR = typeof navigator !== 'undefined';
const IS_TOUCH = HAS_WINDOW
  && ('ontouchstart' in window || (HAS_NAVIGATOR && navigator.msMaxTouchPoints > 0));
const EVENTS = IS_TOUCH ? ['touchstart'] : ['click'];

function processDirectiveArguments(bindingValue) {
  const isFunction = typeof bindingValue === 'function';
  if (!isFunction && typeof bindingValue !== 'object') {
    throw new Error('clickOutside: binding value must be a function or an object');
  }

  return {
    handler: isFunction ? bindingValue : bindingValue.handler,
    middleware: bindingValue.middleware || ((item) => item),
    events: bindingValue.events || EVENTS,
    isActive: !(bindingValue.isActive === false),
    detectIframe: !(bindingValue.detectIframe === false),
    capture: !!bindingValue.capture,
  };
}

function execHandler({ event, handler, middleware }) {
  if (middleware(event)) {
    handler(event);
  }
}

function onFauxIframeClick({
  el, event, handler, middleware,
}) {
  // On firefox clicking an iframe triggers blur, but the iframe only becomes
  // document.activeElement on the next event loop.
  setTimeout(() => {
    const { activeElement } = document;
    if (activeElement && activeElement.tagName === 'IFRAME' && !el.contains(activeElement)) {
      execHandler({ event, handler, middleware });
    }
  }, 0);
}

function onEvent({
  el, event, handler, middleware,
}) {
  const path = (event.composedPath && event.composedPath()) || event.path;
  const isClickOutside = path ? path.indexOf(el) < 0 : !el.contains(event.target);

  if (!isClickOutside) {
    return;
  }

  execHandler({ event, handler, middleware });
}

function bind(el, { value }) {
  const {
    events, handler, middleware, isActive, detectIframe, capture,
  } = processDirectiveArguments(value);

  if (!isActive) {
    return;
  }

  el[HANDLERS_PROPERTY] = events.map((eventName) => ({
    event: eventName,
    srcTarget: document.documentElement,
    handler: (event) => onEvent({
      el, event, handler, middleware,
    }),
    capture,
  }));

  if (detectIframe) {
    el[HANDLERS_PROPERTY].push({
      event: 'blur',
      srcTarget: window,
      handler: (event) => onFauxIframeClick({
        el, event, handler, middleware,
      }),
      capture,
    });
  }

  // Deferred, so the very click that mounted the element does not immediately
  // trigger the handler again.
  el[HANDLERS_PROPERTY].forEach((entry) => setTimeout(() => {
    if (!el[HANDLERS_PROPERTY]) {
      return;
    }
    entry.srcTarget.addEventListener(entry.event, entry.handler, entry.capture);
  }, 0));
}

function unbind(el) {
  const handlers = el[HANDLERS_PROPERTY] || [];
  handlers.forEach(({
    event, srcTarget, handler, capture,
  }) => srcTarget.removeEventListener(event, handler, capture));
  delete el[HANDLERS_PROPERTY];
}

export const clickOutside = {
  mounted: bind,
  updated(el, { value, oldValue }) {
    if (JSON.stringify(value) === JSON.stringify(oldValue)) {
      return;
    }
    unbind(el);
    bind(el, { value });
  },
  unmounted: unbind,
};

export default clickOutside;
